import React, { useRef } from 'react';
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { EventWordmark } from '../../EventWordmark';
import { ventureOpening } from '../../../data/ventureStory';
import { useLanguage } from '../../../contexts/LanguageContext';
const ease = [0.22, 1, 0.36, 1] as const;

// Picks up where the 360° walk-through ends: the club, named, then a wide film frame opening up
export function EditorialOpening() {
  const reduce = useReducedMotion();
  const frameRef = useRef<HTMLDivElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: frameRef,
    offset: ['start end', 'end start']
  });
  const clip = useTransform(scrollYProgress, [0, 0.45], reduce ? ['inset(0% 0% 0% 0%)', 'inset(0% 0% 0% 0%)'] : ['inset(10% 8% 10% 8%)', 'inset(0% 0% 0% 0%)']);
  // A gentle settle only — scaling a still image up softens it
  const scale = useTransform(scrollYProgress, [0, 1], reduce ? [1, 1] : [1.05, 1]);
  const {
    tr
  } = useLanguage();
  // Translated as a whole sentence, then split for the word-by-word reveal
  const words = tr(ventureOpening.title).split(' ');
  return <section aria-labelledby="venture-opening" className="relative" data-id="element-1242" data-mp="2g29xa9kdf68n">
      <div className="mx-auto max-w-[1440px] px-6 pb-16 pt-36 md:px-16 md:pb-24 md:pt-48" data-id="element-1243" data-mp="2byvg3fkp4jtm">
        <motion.p initial={{
        opacity: 0,
        y: reduce ? 0 : 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1.2,
        ease,
        delay: 0.5
      }} className="flex items-center gap-4 text-[14px] font-light text-paper/70" data-id="element-1244" data-mp="270k531io039t">
          <EventWordmark name="Venturist" className="text-[40px] text-paper" data-id="element-1245" data-mp="1x8e2yyyp85gw" />
          <span className="h-px w-10 bg-paper/30" aria-hidden="true" data-id="element-1246" data-mp="1sbxfm6zbpv4v" />
          {ventureOpening.kicker}
        </motion.p>

        <div className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:items-end" data-id="element-1247" data-mp="1o7proc0owj7p">
          <h1 id="venture-opening" data-i18n-skip className="font-editorial text-[64px] leading-[0.95] tracking-[-0.025em] md:text-[124px] lg:col-span-8 lg:text-[150px]" data-id="element-1248" data-mp="1j9xwn94r0gza">
            {words.map((w, i) => <span key={`${w}-${i}`} className="inline-block overflow-hidden pb-2 align-bottom" data-id="element-1249" data-mp="14q3mnd8daet6" data-mpi={i}>
                <motion.span className="inline-block" initial={{
              y: reduce ? 0 : '105%',
              opacity: reduce ? 0 : 1
            }} animate={{
              y: 0,
              opacity: 1
            }} transition={{
              duration: 1.2,
              ease,
              delay: 0.7 + i * 0.07
            }} data-id="element-1250" data-mp="19l0syce9ia2d">
                  {w}&nbsp;
                </motion.span>
              </span>)}
          </h1>
          <motion.p initial={{
          opacity: 0,
          y: reduce ? 0 : 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1.2,
          ease,
          delay: 1.3
        }} className="max-w-[420px] text-[18px] font-light leading-[30px] text-paper/80 lg:col-span-4 lg:pb-6" data-id="element-1251" data-mp="1sfo8c3vwnq65">
            {ventureOpening.body}
          </motion.p>
        </div>
      </div>

      {/* Held at a size the photograph renders crisply, lifted off the page by shadow rather than a frame */}
      <div className="px-6 md:px-16" data-id="element-1252" data-mp="3vsezp6pijqkw">
        <motion.div ref={frameRef} style={{
        clipPath: clip
      }} className="group relative mx-auto aspect-[4/5] max-w-[1180px] overflow-hidden shadow-[0_60px_120px_-30px_rgba(0,0,0,0.9)] sm:aspect-[16/9]" data-id="element-1253" data-mp="3qvx2crwiiuil">
          <motion.img src={ventureOpening.image} alt={ventureOpening.alt} style={{
          scale
        }} className="h-full w-full object-cover [filter:contrast(1.06)_saturate(1.05)]" data-id="element-1254" data-mp="4gc7ucbo02nj" />
          <div className="absolute inset-0 bg-ink/10 transition-colors duration-500 group-hover:bg-ink/0" aria-hidden="true" data-id="element-1255" data-mp="3lwc12atvea2p" />
        </motion.div>
      </div>
    </section>;
}